import { useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Search, UtensilsCrossed, Store, ShoppingCart, Sparkles,
  ArrowRight, PiggyBank,
} from "lucide-react";
import { relocationApi } from "@/api/relocation";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* Phase 30 · Slice A — Food & Fun module with the "Pivot & Save" Multi-Stop
   Engine: a zero-result culinary/grocery query is rescued into an optimised
   Specialty-Shop + Supermarket errand with computed savings. */
export default function FoodFunDesk({ onBack }) {
  const [query, setQuery] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);

  const search = async () => {
    if (!query.trim()) { toast.error("Type something to search"); return; }
    setBusy(true); setRes(null);
    try { setRes(await relocationApi.foodSearch(query.trim())); }
    catch { toast.error("Search failed"); }
    finally { setBusy(false); }
  };

  const pivot = res?.pivot;
  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-cream" data-testid="foodfun-desk">
      <header className="shrink-0 flex items-center gap-3 border-b-2 border-charcoal/10 px-4 pt-4 pb-3">
        <button onClick={onBack} data-testid="foodfun-back" className="grid h-10 w-10 place-items-center rounded-full border-2 border-charcoal bg-white shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} strokeWidth={2.4} /></button>
        <div className="leading-tight">
          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/50">Food & Fun</p>
          <h1 className="font-serif text-2xl text-charcoal flex items-center gap-1.5"><UtensilsCrossed size={18} /> Culinary Finder</h1>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pt-4 pb-28 space-y-3">
        <div className="flex gap-2">
          <input value={query} onChange={(e) => setQuery(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()}
            data-testid="foodfun-input" placeholder="Try 'saffron' or 'milk'…"
            className="flex-1 bg-white border-2 border-charcoal rounded-xl px-3 py-2.5 font-mono text-xs outline-none focus:ring-2 focus:ring-charcoal" />
          <button onClick={search} disabled={busy} data-testid="foodfun-search" className="grid w-12 place-items-center rounded-xl border-2 border-charcoal bg-gold shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
          </button>
        </div>

        {res && !res.zero_result && (
          <div className="rounded-2xl border-2 border-charcoal bg-white p-3 shadow-[3px_3px_0px_#1A1A1A]" data-testid="foodfun-result">
            <p className="font-mono text-[10px] uppercase tracking-widest text-emerald-600">Direct match</p>
            <p className="font-sans text-[15px] font-bold text-charcoal">{res.result.item}</p>
            <p className="font-mono text-[11px] text-charcoal/60">{res.result.store} · {money(res.result.est_price)}</p>
          </div>
        )}

        {pivot && (
          <div className="space-y-3" data-testid="foodfun-pivot">
            <div className="rounded-2xl border-2 border-gold bg-gold/10 p-3">
              <p className="flex items-center gap-1.5 font-mono text-[9px] font-black uppercase tracking-widest text-charcoal"><Sparkles size={12} /> Pivot & Save Engine</p>
              <p className="mt-1 font-mono text-[11px] leading-snug text-charcoal/75">{pivot.headline}</p>
            </div>

            <div className="flex items-stretch gap-2" data-testid="foodfun-stops">
              {pivot.stops.map((s, i) => (
                <div key={i} className="flex-1 flex items-center gap-2">
                  <div className="flex-1 rounded-2xl border-2 border-charcoal bg-white p-2.5 shadow-[3px_3px_0px_#1A1A1A]">
                    <span className="grid h-8 w-8 place-items-center rounded-lg border-2 border-charcoal bg-charcoal text-gold">{s.type === "Supermarket" ? <ShoppingCart size={15} /> : <Store size={15} />}</span>
                    <p className="mt-1.5 font-mono text-[8px] uppercase tracking-widest text-charcoal/50">Stop {s.order} · {s.type}</p>
                    <p className="font-sans text-[13px] font-bold leading-tight text-charcoal">{s.store}</p>
                    <p className="font-mono text-[9px] text-charcoal/55 truncate">{s.items.join(", ")}</p>
                    <p className="mt-1 font-mono text-[12px] font-black text-charcoal">{money(s.est_cost)}</p>
                  </div>
                  {i < pivot.stops.length - 1 && <ArrowRight size={16} className="shrink-0 text-charcoal/40" />}
                </div>
              ))}
            </div>

            <div className="rounded-2xl border-2 border-emerald-600 bg-emerald-50 p-3" data-testid="foodfun-savings">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 font-mono text-[10px] font-black uppercase tracking-widest text-emerald-700"><PiggyBank size={14} /> You save</span>
                <span className="font-serif text-2xl font-bold text-emerald-700">{money(pivot.savings)}</span>
              </div>
              <p className="mt-1 font-mono text-[9px] text-emerald-800/70">Multi-stop {money(pivot.multi_stop_total)} vs single-store {money(pivot.single_store_total)} — {pivot.savings_pct}% cheaper.</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
